import { useEffect, useCallback, useRef } from 'react';
import { useLocation } from 'react-router-dom';
import trackingService from '../services/apiTrackingService';

/**
 * Custom hook for page view and event tracking
 * Automatically tracks page views and provides event tracking functions
 */
export const useTracking = () => {
  const location = useLocation();
  const previousPath = useRef(null);

  // Track page views automatically when location changes
  useEffect(() => {
    const currentPath = location.pathname + location.search;
    
    // Track page exit for previous page if it exists
    if (previousPath.current && previousPath.current !== currentPath) {
      trackingService.trackPageExit(previousPath.current);
    }

    // Track new page view
    trackingService.trackPageView(
      currentPath,
      document.title,
      document.referrer
    );

    // Update previous path
    previousPath.current = currentPath;

    // Track page exit when component unmounts or page unloads
    const handleBeforeUnload = () => {
      trackingService.trackPageExit(currentPath);
    };

    window.addEventListener('beforeunload', handleBeforeUnload);
    
    return () => {
      window.removeEventListener('beforeunload', handleBeforeUnload);
    };
  }, [location]);

  // Event tracking functions
  const trackEvent = useCallback((eventName, eventCategory, eventData = {}) => {
    return trackingService.trackEvent(eventName, eventCategory, eventData);
  }, []);

  const trackDraftRoomEvent = useCallback((eventName, eventData = {}) => {
    return trackingService.trackDraftRoomEvent(eventName, eventData);
  }, []);

  const trackUserInteraction = useCallback((action, element, additionalData = {}) => {
    return trackingService.trackUserInteraction(action, element, additionalData);
  }, []);

  const trackLeagueEvent = useCallback((eventName, leagueId, eventData = {}) => {
    return trackingService.trackLeagueEvent(eventName, leagueId, eventData);
  }, []);

  return {
    trackEvent,
    trackDraftRoomEvent,
    trackUserInteraction,
    trackLeagueEvent,
    trackingService
  };
};

/**
 * Hook for component-specific tracking
 * Use this when you want to track events specific to a component
 */
export const useComponentTracking = (componentName) => {
  const { trackEvent, trackUserInteraction } = useTracking();

  const trackComponentEvent = useCallback((eventName, eventData = {}) => {
    return trackEvent(eventName, 'component', {
      component: componentName,
      ...eventData
    });
  }, [trackEvent, componentName]);

  const trackComponentInteraction = useCallback((action, element, additionalData = {}) => {
    return trackUserInteraction(action, element, {
      component: componentName,
      ...additionalData
    });
  }, [trackUserInteraction, componentName]);

  // Track component mount
  useEffect(() => {
    trackComponentEvent('component_mounted');
    
    return () => {
      trackComponentEvent('component_unmounted');
    };
  }, [trackComponentEvent]);

  return {
    trackComponentEvent,
    trackComponentInteraction
  };
};

/**
 * Hook for draft room specific tracking
 */
export const useDraftRoomTracking = (leagueId, draftSessionId) => {
  const { trackDraftRoomEvent } = useTracking();

  const trackBid = useCallback((playerId, bidAmount, isWinning = false) => {
    return trackDraftRoomEvent('bid_placed', {
      league_id: leagueId,
      draft_session_id: draftSessionId,
      player_id: playerId,
      bid_amount: bidAmount,
      is_winning: isWinning
    });
  }, [trackDraftRoomEvent, leagueId, draftSessionId]);

  const trackNomination = useCallback((playerId) => {
    return trackDraftRoomEvent('player_nominated', {
      league_id: leagueId,
      draft_session_id: draftSessionId,
      player_id: playerId
    });
  }, [trackDraftRoomEvent, leagueId, draftSessionId]);

  const trackWatchlistAction = useCallback((action, playerId) => {
    return trackDraftRoomEvent('watchlist_action', {
      league_id: leagueId,
      draft_session_id: draftSessionId,
      action, // 'add' or 'remove'
      player_id: playerId
    });
  }, [trackDraftRoomEvent, leagueId, draftSessionId]);

  const trackFilterChange = useCallback((filterType, filterValue) => {
    return trackDraftRoomEvent('filter_changed', {
      league_id: leagueId,
      draft_session_id: draftSessionId,
      filter_type: filterType,
      filter_value: filterValue
    });
  }, [trackDraftRoomEvent, leagueId, draftSessionId]);

  const trackDraftRoomJoin = useCallback(() => {
    return trackDraftRoomEvent('draft_room_joined', {
      league_id: leagueId,
      draft_session_id: draftSessionId
    });
  }, [trackDraftRoomEvent, leagueId, draftSessionId]);

  const trackDraftRoomLeave = useCallback(() => {
    return trackDraftRoomEvent('draft_room_left', {
      league_id: leagueId,
      draft_session_id: draftSessionId
    });
  }, [trackDraftRoomEvent, leagueId, draftSessionId]);

  return {
    trackBid,
    trackNomination,
    trackWatchlistAction,
    trackFilterChange,
    trackDraftRoomJoin,
    trackDraftRoomLeave
  };
};

export default useTracking;